import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, FilePlus, Pencil, Save, Trash2 } from 'lucide-react';

import styles from './DashboardSelector.module.css';
import { DeleteDashboardModal } from './DeleteDashboardModal';
import { NamePromptModal } from './NamePromptModal';
import type { MarketMonitorDashboardState } from './MarketMonitorDashboardProvider';

const NEW_LABEL = 'Untitled';

interface PendingPrompt {
  title: string;
  label: string;
  initialValue: string;
  confirmLabel: string;
  resolve: (value: string | null) => void;
}

interface PendingDelete {
  id: string;
  name: string;
}

export interface DashboardSelectorProps {
  state: MarketMonitorDashboardState;
}

export const DashboardSelector: React.FC<DashboardSelectorProps> = ({ state }) => {
  const {
    list,
    isListLoading,
    activeId,
    activeName,
    isLoadingActive,
    isSaving,
    isDirty,
    error,
    open,
    newDashboard,
    save,
    saveAs,
    rename,
    remove,
  } = state;

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [pendingPrompt, setPendingPrompt] = useState<PendingPrompt | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteFailed, setDeleteFailed] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const promptName = useCallback(
    (initial: string, action: string): Promise<string | null> =>
      new Promise<string | null>((resolve) => {
        setPendingPrompt({
          title: `${action} dashboard`,
          label: `${action} dashboard name`,
          initialValue: initial,
          confirmLabel: 'Save',
          resolve,
        });
      }),
    [],
  );

  const resolvePrompt = useCallback(
    (value: string | null) => {
      if (!pendingPrompt) return;
      pendingPrompt.resolve(value);
      setPendingPrompt(null);
    },
    [pendingPrompt],
  );

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!containerRef.current) return;
      if (target && containerRef.current.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [menuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const handleOpen = useCallback(
    async (id: string) => {
      closeMenu();
      await open(id);
    },
    [open, closeMenu],
  );

  const handleNew = useCallback(() => {
    closeMenu();
    newDashboard();
  }, [newDashboard, closeMenu]);

  const handleSave = useCallback(async () => {
    if (!activeId) {
      const name = await promptName('My Dashboard', 'Save new');
      if (!name) return;
      await saveAs(name);
      return;
    }
    await save();
  }, [activeId, save, saveAs, promptName]);

  const handleSaveAs = useCallback(async () => {
    const name = await promptName(activeName ?? '', 'Save copy as');
    if (!name) return;
    await saveAs(name);
  }, [saveAs, activeName, promptName]);

  const handleRename = useCallback(
    async (id: string, currentName: string) => {
      const next = await promptName(currentName, 'Rename');
      if (!next || next === currentName) return;
      await rename(id, next);
    },
    [rename, promptName],
  );

  const requestDelete = useCallback(
    (id: string, currentName: string) => {
      closeMenu();
      setDeleteFailed(false);
      setPendingDelete({ id, name: currentName });
    },
    [closeMenu],
  );

  const cancelDelete = useCallback(() => {
    if (isDeleting) return;
    setPendingDelete(null);
    setDeleteFailed(false);
  }, [isDeleting]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    setDeleteFailed(false);
    const ok = await remove(pendingDelete.id);
    setIsDeleting(false);
    if (!ok) {
      setDeleteFailed(true);
      return;
    }
    setPendingDelete(null);
  }, [pendingDelete, remove]);

  const triggerLabel = activeName ?? NEW_LABEL;
  const saveDisabled = isSaving || isLoadingActive;
  const triggerTitle = isDirty
    ? `${activeName ?? 'Unsaved dashboard'} (unsaved changes)`
    : (activeName ?? 'Select a dashboard');

  return (
    <div className={styles.selector} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={triggerTitle}
        data-testid="dashboard-selector-trigger"
        data-dirty={isDirty || undefined}
      >
        <span className={styles.triggerLabel}>{triggerLabel}</span>
        {isDirty && (
          <span
            className={styles.dirtyDot}
            aria-label="unsaved changes"
            data-testid="dashboard-selector-dirty"
          />
        )}
        <span className={styles.triggerCaret}>
          <ChevronDown size={14} aria-hidden="true" />
        </span>
      </button>

      <button
        type="button"
        className={styles.actionButton}
        onClick={handleSave}
        disabled={saveDisabled}
        title={activeId ? 'Save changes' : 'Save as new dashboard'}
        data-testid="dashboard-selector-save"
      >
        <Save size={13} aria-hidden="true" />
        <span>{isSaving ? 'Saving…' : 'Save'}</span>
      </button>

      <button
        type="button"
        className={styles.actionButton}
        onClick={handleSaveAs}
        disabled={saveDisabled}
        title="Save as a new dashboard"
        data-testid="dashboard-selector-save-as"
      >
        <span>Save as…</span>
      </button>

      <button
        type="button"
        className={styles.iconActionButton}
        onClick={() => activeId && activeName && requestDelete(activeId, activeName)}
        disabled={saveDisabled || !activeId}
        title={activeId ? 'Delete this dashboard' : 'Save the dashboard before deleting'}
        aria-label="Delete dashboard"
        data-testid="dashboard-selector-delete-active"
      >
        <Trash2 size={14} aria-hidden="true" />
      </button>

      {(isListLoading || isLoadingActive) && (
        <span className={styles.statusText}>Loading…</span>
      )}
      {error && <span className={styles.errorText} title={error}>{error}</span>}

      {menuOpen && (
        <div className={styles.menu} role="menu" data-testid="dashboard-selector-menu">
          <button
            type="button"
            className={styles.menuHeaderItem}
            onClick={handleNew}
            role="menuitem"
            data-testid="dashboard-selector-new"
          >
            <FilePlus size={14} aria-hidden="true" />
            <span>New dashboard</span>
          </button>
          <div className={styles.menuDivider} />
          {list.length === 0 ? (
            <div className={styles.menuEmpty}>No saved dashboards yet.</div>
          ) : (
            list.map((summary) => {
              const isActive = summary.id === activeId;
              return (
                <div
                  key={summary.id}
                  className={`${styles.menuItem} ${isActive ? styles.menuItemActive : ''}`}
                  role="menuitem"
                >
                  <button
                    type="button"
                    className={styles.menuOpenButton}
                    onClick={() => handleOpen(summary.id)}
                    title={summary.name}
                    data-testid="dashboard-selector-open"
                  >
                    <span className={styles.menuItemIcon}>
                      {isActive ? (
                        <Check size={13} aria-hidden="true" />
                      ) : (
                        <span style={{ width: 13, display: 'inline-block' }} />
                      )}
                    </span>
                    <span>{summary.name}</span>
                  </button>
                  <button
                    type="button"
                    className={styles.menuIconButton}
                    onClick={() => handleRename(summary.id, summary.name)}
                    aria-label={`Rename dashboard ${summary.name}`}
                    title="Rename"
                    data-testid="dashboard-selector-rename"
                  >
                    <Pencil size={13} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={styles.menuIconButton}
                    onClick={() => requestDelete(summary.id, summary.name)}
                    aria-label={`Delete dashboard ${summary.name}`}
                    title="Delete"
                    data-testid="dashboard-selector-delete"
                  >
                    <Trash2 size={13} aria-hidden="true" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      <NamePromptModal
        open={pendingPrompt !== null}
        title={pendingPrompt?.title ?? ''}
        label={pendingPrompt?.label ?? ''}
        initialValue={pendingPrompt?.initialValue ?? ''}
        confirmLabel={pendingPrompt?.confirmLabel ?? 'OK'}
        onCancel={() => resolvePrompt(null)}
        onConfirm={(name) => resolvePrompt(name)}
      />

      <DeleteDashboardModal
        open={pendingDelete !== null}
        dashboardName={pendingDelete?.name ?? ''}
        deleting={isDeleting}
        errorMessage={deleteFailed ? error : null}
        onCancel={cancelDelete}
        onConfirm={() => {
          void confirmDelete();
        }}
      />
    </div>
  );
};
